// pages/TraitementFinAnneePage.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, AlertCircle, CheckCircle, Clock, TrendingUp } from 'lucide-react';

export default function TraitementFinAnneePage() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [info, setInfo] = useState({
    anneeCourante: new Date().getFullYear(),
    nouvelleAnnee: new Date().getFullYear() + 1,
    estFinAnnee: false,
    joursRestants: 0
  });

  useEffect(() => {
    const aujourdhui = new Date();
    const finAnnee = new Date(aujourdhui.getFullYear(), 11, 31);
    const diffTime = finAnnee - aujourdhui;
    const diffJours = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    setInfo({
      anneeCourante: aujourdhui.getFullYear(),
      nouvelleAnnee: aujourdhui.getFullYear() + 1,
      estFinAnnee: diffJours <= 1 && diffJours >= 0,
      joursRestants: diffJours
    });
  }, []);

  const executerTraitement = async () => {
    if (!window.confirm(`⚠️ Attention !\n\nCe traitement va initialiser les soldes pour l'année ${info.nouvelleAnnee}.\n\nÊtes-vous sûr de vouloir continuer ?`)) {
      return;
    }
    
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      const res = await axios.post('/api/admin/initialiser-soldes-annuelle');
      if (res.data.succes === 1) {
        setMessage({ type: 'success', text: res.data.message });
      } else {
        setMessage({ type: 'error', text: res.data.message });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Erreur lors du traitement' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* En-tête */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">📅 Traitement de fin d'année</h2>
          <p className="text-sm text-gray-500 mt-1">
            Initialisation des soldes pour l'année {info.nouvelleAnnee}
          </p>
        </div>
      </div>

      {/* Informations */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Calendar className="text-purple-600" size={24} />
          <h3 className="text-lg font-semibold text-gray-800">Informations</h3>
        </div>
        
        <div className="space-y-3 text-sm">
          <div className="flex justify-between py-2 border-b">
            <span className="text-gray-500">Année en cours :</span>
            <span className="font-semibold">{info.anneeCourante}</span>
          </div>
          <div className="flex justify-between py-2 border-b">
            <span className="text-gray-500">Nouvelle année :</span>
            <span className="font-semibold text-purple-600">{info.nouvelleAnnee}</span>
          </div>
          <div className="flex justify-between py-2 border-b">
            <span className="text-gray-500">Jours restants avant fin d'année :</span>
            <span className={`font-semibold ${info.joursRestants <= 7 ? 'text-red-600' : 'text-gray-700'}`}>
              {info.joursRestants} jours
            </span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-gray-500">Statut :</span>
            {info.estFinAnnee ? (
              <span className="inline-flex items-center gap-1 text-green-600"><CheckCircle size={14} /> Prêt pour le traitement</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-orange-600"><Clock size={14} /> En attente (31 décembre)</span>
            )}
          </div>
        </div>
      </div>

      {/* Message d'alerte */}
      {!info.estFinAnnee && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-yellow-600 mt-0.5" size={18} />
            <div className="text-sm text-yellow-700">
              <p className="font-semibold">⚠️ Traitement non disponible</p>
              <p className="mt-1">
                Le traitement de fin d'année ne peut être exécuté que le <strong>31 décembre</strong>.
                {info.joursRestants > 0 && ` Plus que ${info.joursRestants} jours avant cette date.`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Zone de traitement */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="text-purple-600" size={24} />
          <h3 className="text-lg font-semibold text-gray-800">Exécution du traitement</h3>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm text-gray-600">
          <p className="font-medium mb-2">Ce traitement va :</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Initialiser les soldes pour l'année {info.nouvelleAnnee}</li>
            <li>Créer une ligne par employé actif et par type de congé</li>
            <li>Pour le congé annuel : calcul selon la règle Maroc</li>
            <li>Pour les autres types : utilisation de la valeur par défaut</li>
          </ul>
        </div>
        
        {message.text && (
          <div className={`p-3 rounded-lg mb-4 flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            {message.text}
          </div>
        )}
        
        <button
          onClick={executerTraitement}
          disabled={loading || !info.estFinAnnee}
          className={`w-full py-2 rounded-lg text-white font-medium transition flex items-center justify-center gap-2 ${
            info.estFinAnnee
              ? 'bg-purple-600 hover:bg-purple-700'
              : 'bg-gray-400 cursor-not-allowed'
          }`}
        >
          {loading ? (
            <>⏳ Traitement en cours...</>
          ) : (
            <>🚀 Initialiser les soldes pour {info.nouvelleAnnee}</>
          )}
        </button>
      </div>
    </div>
  );
}